/**
 * Centralized Query Keys Factory for TanStack Query
 *
 * This file defines all query keys used throughout the application.
 * Following a hierarchical structure ensures proper cache invalidation
 * and prevents key collisions.
 *
 * Pattern:
 * - all: Base key for entity type
 * - lists: All list queries for entity
 * - list(filters): Specific list query with filters
 * - details: All detail queries for entity
 * - detail(id): Specific detail query for an entity
 */

export const queryKeys = {
  // Tenants Module
  tenants: {
    all: ["tenants"] as const,
    lists: () => [...queryKeys.tenants.all, "list"] as const,
    list: (filters?: any) => [...queryKeys.tenants.lists(), filters] as const,
    details: () => [...queryKeys.tenants.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.tenants.details(), id] as const,
    organization: () => [...queryKeys.tenants.all, "organization"] as const,
  },

  // Properties Module
  properties: {
    all: ["properties"] as const,
    lists: () => [...queryKeys.properties.all, "list"] as const,
    list: (filters?: any) =>
      [...queryKeys.properties.lists(), filters] as const,
    details: () => [...queryKeys.properties.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.properties.details(), id] as const,
    usages: () => [...queryKeys.properties.all, "usages"] as const,
    organization: () => [...queryKeys.properties.all, "organization"] as const,
  },

  // Landlords Module
  landlords: {
    all: ["landlords"] as const,
    lists: () => [...queryKeys.landlords.all, "list"] as const,
    list: (filters?: any) => [...queryKeys.landlords.lists(), filters] as const,
    details: () => [...queryKeys.landlords.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.landlords.details(), id] as const,
  },

  // Tenant Reports Module
  tenantReports: {
    all: ["tenant-reports"] as const,
    lists: () => [...queryKeys.tenantReports.all, "list"] as const,
    list: (filters?: any) =>
      [...queryKeys.tenantReports.lists(), filters] as const,
    details: () => [...queryKeys.tenantReports.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.tenantReports.details(), id] as const,
    byProperty: (propertyId: string) =>
      [...queryKeys.tenantReports.all, "property", propertyId] as const,
    publicLink: (id: string) =>
      [...queryKeys.tenantReports.detail(id), "public-link"] as const,
    webData: (id: string) =>
      [...queryKeys.tenantReports.detail(id), "web-data"] as const,
    publicData: (token: string) =>
      [...queryKeys.tenantReports.all, "public-data", token] as const,
    contractDetails: (folderId: string) =>
      [...queryKeys.tenantReports.all, "contract-details", folderId] as const,
  },

  // Investigations Module
  investigations: {
    all: ["investigations"] as const,
    lists: () => [...queryKeys.investigations.all, "list"] as const,
    list: (filters?: any) =>
      [...queryKeys.investigations.lists(), filters] as const,
    details: () => [...queryKeys.investigations.all, "detail"] as const,
    detail: (id: string) =>
      [...queryKeys.investigations.details(), id] as const,
  },

  // Contracts Module
  contracts: {
    all: ["contracts"] as const,
    lists: () => [...queryKeys.contracts.all, "list"] as const,
    list: (filters?: any) => [...queryKeys.contracts.lists(), filters] as const,
    details: () => [...queryKeys.contracts.all, "detail"] as const,
    detail: (id: string) => [...queryKeys.contracts.details(), id] as const,
    clauses: (contractId: string) =>
      [...queryKeys.contracts.detail(contractId), "clauses"] as const,
    baseClauses: (filters?: any) =>
      [...queryKeys.contracts.all, "base-clauses", filters] as const,
    // Semilla de clausulado de renovación (MB-379). Va por FOLIO y no por contrato: se pide
    // antes de que el contrato exista, mientras se arma el documento del editor.
    renewalClauseSeed: (folioNumber: string) =>
      [...queryKeys.contracts.all, "renewal-clause-seed", folioNumber] as const,
  },

  // Organization Module
  organization: {
    all: ["organization"] as const,
    data: () => [...queryKeys.organization.all, "data"] as const,
    members: () => [...queryKeys.organization.all, "members"] as const,
    credits: () => [...queryKeys.organization.all, "credits"] as const,
    apiKeys: () => [...queryKeys.organization.all, "api-keys"] as const,
    reports: (filters?: any) => {
      if (filters == null) {
        return [...queryKeys.organization.all, "reports"] as const;
      }
      return [...queryKeys.organization.all, "reports", filters] as const;
    },
    tenantReports: {
      all: () => [...queryKeys.organization.all, "tenant-reports"] as const,
      list: (filters?: any) =>
        [...queryKeys.organization.all, "tenant-reports", filters] as const,
    },
  },

  // Membership Module
  membership: {
    all: ["membership"] as const,
    details: (id: string) =>
      [...queryKeys.membership.all, "detail", id] as const,
    plans: () => [...queryKeys.membership.all, "plans"] as const,
  },

  // Payments Module
  payments: {
    all: ["payments"] as const,
    cards: () => [...queryKeys.payments.all, "cards"] as const,
    history: (filters?: any) =>
      [...queryKeys.payments.all, "history", filters] as const,
    chargeInfo: (resourceType: string, resourceId: string) =>
      [
        ...queryKeys.payments.all,
        "chargeInfo",
        resourceType,
        resourceId,
      ] as const,
  },

  // Quotas Module
  quotas: {
    all: ["quotas"] as const,
    packages: () => [...queryKeys.quotas.all, "packages"] as const,
    orders: {
      all: () => [...queryKeys.quotas.all, "orders"] as const,
      list: (filters?: any) =>
        [...queryKeys.quotas.orders.all(), "list", filters] as const,
      detail: (id: string) =>
        [...queryKeys.quotas.orders.all(), "detail", id] as const,
    },
    transactions: {
      all: () => [...queryKeys.quotas.all, "transactions"] as const,
      list: (filters?: any) =>
        [...queryKeys.quotas.transactions.all(), "list", filters] as const,
    },
  },

  // Notifications Module
  notifications: {
    all: ["notifications"] as const,
    list: () => [...queryKeys.notifications.all, "list"] as const,
    settings: () => [...queryKeys.notifications.all, "settings"] as const,
  },

  // Accounts Module
  accounts: {
    all: ["accounts"] as const,
    advisor: () => [...queryKeys.accounts.all, "advisor"] as const,
    credits: () => [...queryKeys.accounts.all, "credits"] as const,
    tokens: () => [...queryKeys.accounts.all, "tokens"] as const,
  },

  // Express Reports Module
  expressReports: {
    all: ["express-reports"] as const,
    lists: () => [...queryKeys.expressReports.all, "list"] as const,
    list: (filters?: any) =>
      [...queryKeys.expressReports.lists(), filters] as const,
  },

  // Credit Reports Module
  creditReports: {
    all: ["credit-reports"] as const,
    detail: (id: string) =>
      [...queryKeys.creditReports.all, "detail", id] as const,
  },

  // Guarantors Module
  guarantors: {
    all: ["guarantors"] as const,
    lists: () => [...queryKeys.guarantors.all, "list"] as const,
    detail: (id: string) =>
      [...queryKeys.guarantors.all, "detail", id] as const,
  },

  // Referrals Module
  referrals: {
    all: ["referrals"] as const,
    code: () => [...queryKeys.referrals.all, "code"] as const,
    invitationLists: () => [...queryKeys.referrals.all, "invitations"] as const,
    invitations: (filters?: any) =>
      [...queryKeys.referrals.invitationLists(), filters] as const,
  },

  // Web Check Module
  webCheck: {
    all: ["web-check"] as const,
    results: (tenantReportId: string) =>
      [...queryKeys.webCheck.all, "results", tenantReportId] as const,
    complianceStatus: (tenantReportId: string) =>
      [...queryKeys.webCheck.all, "compliance-status", tenantReportId] as const,
  },

  // Zip Codes Module (SEPOMEX lookup - used only by the buro authorization
  // address flow; see AddressFormSepomex)
  zipCodes: {
    all: ["zip-codes"] as const,
    detail: (code: string) => [...queryKeys.zipCodes.all, code] as const,
  },
};
